import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { AccessTime } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { AvatarImage } from '~/shared/components/AvatarImage';

export const avatarSizePx = 40;

export const StyledCardWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  height: '100%',
  display: 'block',
  textAlign: 'left',
}));

export const StyledCardLink = styled(RouterLink)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  '&:focus': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
  '&:focus:not(:focus-visible)': {
    outline: 'none',
  },
}));

export const StyledContentWrapper = styled(Box)({
  position: 'relative',
  zIndex: 2,
  width: '100%',
  pointerEvents: 'none',
});

export const StyledHeader = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  minHeight: 36,
  marginBottom: theme.spacing(1.25),
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    paddingTop: 0,
  },
}));

/** Wrapper for status dropdown + badge so dropdown is clickable (card content has pointer-events: none). */
export const HeaderLeftBlock = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
  minWidth: 0,
  pointerEvents: 'auto',
}));

export const StyledMatterStatusSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== '$bgcolor' && prop !== '$color',
})<{ $bgcolor: string; $color: string }>(({ theme, $bgcolor, $color }) => ({
  height: 32,
  fontWeight: 600,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: $bgcolor,
  color: $color,
  '& .MuiSelect-select': {
    paddingTop: theme.spacing(0.75),
    paddingBottom: theme.spacing(0.75),
    paddingLeft: theme.spacing(1.25),
    paddingRight: theme.spacing(1.25),
    fontSize: theme.typography.caption.fontSize,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.5)',
  },
}));

const CARD_HEADER_CONTROL_HEIGHT = 32;

export const StyledRightControls = styled(Stack)(({ theme }) => ({
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: theme.spacing(1.5),
  flexShrink: 0,
  justifyContent: 'flex-end',
  marginLeft: 'auto',
  flexDirection: 'row',
}));

export const AccessTimeIcon = styled(AccessTime)(({ theme }) => ({
  fontSize: theme.typography.caption.fontSize,
}));

export const InlineBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  minHeight: CARD_HEADER_CONTROL_HEIGHT,
  padding: theme.spacing(0, 1.25),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.action.hover,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 500,
  color: theme.palette.text.secondary,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
}));

export const MessageCountBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    minWidth: 18,
    height: 18,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    transform: 'scale(1) translate(50%, -50%) translate(-4px, 4px)',
    backgroundColor:
      (theme.palette as { red?: { main: string } }).red?.main ?? '#C62828',
    color: theme.palette.common.white,
  },
}));

export const FooterActions = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1.5),
  justifyContent: 'flex-end',
  flexShrink: 0,
}));

export const CardActionButton = styled(Button, {
  shouldForwardProp: (prop) =>
    prop !== '$mobile' && prop !== '$outline' && prop !== '$colorVariant',
})<{
  $mobile?: boolean;
  $outline?: boolean;
  $colorVariant?: 'default' | 'amount';
}>(({ theme, $mobile, $outline, $colorVariant }) => {
  const useAmount = $colorVariant === 'amount';
  const mainColor = useAmount
    ? theme.palette.amount.main
    : theme.palette.darkNavy;
  const hoverBg = useAmount
    ? theme.palette.amount.dark
    : theme.palette.darkNavy;
  const hoverFg = theme.palette.common.white;
  return {
    ...($mobile && { width: '100%' }),
    display: $mobile ? 'inline-flex' : 'none',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: 600,
    textTransform: 'none',
    minHeight: 36,
    padding: theme.spacing(0.75, 1.5),
    ...($outline
      ? {
          backgroundColor: 'transparent',
          color: mainColor,
          borderColor: mainColor,
          '&:hover': {
            backgroundColor: hoverBg,
            color: hoverFg,
            borderColor: hoverBg,
          },
        }
      : {
          backgroundColor: mainColor,
          color: hoverFg,
          borderColor: mainColor,
          '&:hover': {
            backgroundColor: useAmount
              ? theme.palette.amount.dark
              : 'transparent',
            color: useAmount ? hoverFg : theme.palette.darkNavy,
            borderColor: mainColor,
          },
        }),
    ...($mobile && {
      [theme.breakpoints.up('sm')]: { display: 'none' },
    }),
    ...(!$mobile && {
      [theme.breakpoints.up('sm')]: { display: 'inline-flex' },
    }),
  };
});

export const StatsRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  fontSize: theme.typography.caption.fontSize,
}));

export const RetainerStatusMetaLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$color',
})<TypographyProps & { $color: 'red' | 'warning' | 'success' }>(
  ({ theme, $color }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color:
      $color === 'red' ? theme.palette.red.dark : theme.palette[$color].dark,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.43,
    '& .MuiSvgIcon-root': {
      color: 'inherit',
      fontSize: theme.typography.caption.fontSize,
    },
  }),
);

export const MobileActionContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  pointerEvents: 'auto',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

export const StatsItem = styled(Stack, {
  shouldForwardProp: (prop) => prop !== '$alignStart',
})<{ $alignStart?: boolean }>(({ theme, $alignStart }) => ({
  flexDirection: 'row',
  display: 'inline-flex',
  alignItems: $alignStart ? 'flex-start' : 'center',
  gap: theme.spacing(0.5),
  flexWrap: 'nowrap',
  whiteSpace: 'nowrap',
}));

export const IconCircle = styled('span', {
  shouldForwardProp: (prop) => prop !== '$shrink',
})<{ $shrink?: boolean }>(({ theme, $shrink }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 14,
  height: 14,
  minWidth: $shrink ? 14 : undefined,
  minHeight: $shrink ? 14 : undefined,
  flexShrink: $shrink ? 0 : undefined,
  borderRadius: '50%',
  backgroundColor: theme.palette.darkNavy,
  ...($shrink && {
    '& .MuiSvgIcon-root': {
      transform: 'translateY(0.5px)',
    },
  }),
}));

export const AmountHighlight = styled('span')(({ theme }) => ({
  color: theme.palette.amount.main,
}));

export const HeadingTypography = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.h2.fontSize,
  marginBottom: theme.spacing(1),
  lineHeight: 1.2,
  fontWeight: 600,
  [theme.breakpoints.up('sm')]: {
    lineHeight: 1.35,
  },
}));

export const DescriptionTypography = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.25),
  lineHeight: 1.4,
  fontSize: theme.typography.body1.fontSize,
  fontWeight: 400,
}));

export const PlaceholderAvatar = styled(Avatar)(({ theme }) => ({
  width: avatarSizePx,
  height: avatarSizePx,
  flexShrink: 0,
  backgroundColor: theme.palette.grey[300],
  color: theme.palette.grey[600],
}));

export const FooterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  pointerEvents: 'auto',
}));

export const FooterRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}));

export const FooterPartyBlock = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

export const PartyInfoWrapper = styled(Box)({
  minWidth: 0,
});

export const ExpertsBlock = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  minWidth: 0,
});

export const ExpertsList = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  minWidth: 0,
}));

export const ExpertChip = styled(RouterLink)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  textDecoration: 'none',
  color: theme.palette.text.primary,
  borderRadius: 9999,
  padding: theme.spacing(0.5, 1.25),
  backgroundColor: theme.palette.action.hover,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

export const PartyChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  color: theme.palette.text.primary,
  borderRadius: 9999,
  padding: theme.spacing(0.5, 1.25),
  backgroundColor: theme.palette.action.hover,
}));

export const ExpertChipAvatar = styled(AvatarImage)(({ theme }) => ({
  width: 32,
  height: 32,
  fontSize: theme.typography.caption.fontSize,
}));

export const ExpertChipName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$noTruncate',
})<{ $noTruncate?: boolean }>(({ theme, $noTruncate }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 600,
  ...(!$noTruncate && {
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  }),
}));

export const ExpertChipStatus = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$color',
})<
  TypographyProps & {
    $color: 'red' | 'warning' | 'success' | 'default';
  }
>(({ theme, $color }) => ({
  fontSize: theme.typography.caption.fontSize,
  fontWeight: 500,
  color: (() => {
    if ($color === 'red') {
      return theme.palette.red.main;
    }
    if ($color === 'default') {
      return theme.palette.text.secondary;
    }
    return theme.palette[$color].dark;
  })(),
}));

export const StatsLabelTypography = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$lineHeightTight',
})<TypographyProps & { $lineHeightTight?: boolean }>(
  ({ theme, $lineHeightTight }) => ({
    fontSize: theme.typography.caption.fontSize,
    lineHeight: $lineHeightTight
      ? theme.typography.body2.lineHeight
      : theme.typography.caption.lineHeight,
    whiteSpace: 'nowrap',
  }),
);

export const Icon16 = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  fontSize: theme.typography.body1.fontSize,
  '& .MuiSvgIcon-root': {
    fontSize: theme.typography.body1.fontSize,
  },
}));

export const Icon11 = styled('span', {
  shouldForwardProp: (prop) => prop !== '$block',
})<{ $block?: boolean }>(({ theme, $block }) => ({
  display: $block ? 'block' : 'inline-flex',
  fontSize: theme.typography.caption.fontSize,
  '& .MuiSvgIcon-root': {
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.teal.main,
  },
}));

export const StyledMatterCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== '$leftBorderVariant',
})<{
  $leftBorderVariant?: 'orange' | 'red' | 'green' | 'gray' | null;
}>(({ theme, $leftBorderVariant }) => {
  let borderColor: string | undefined;
  if ($leftBorderVariant === 'orange') {
    borderColor = theme.palette.warning.main;
  } else if ($leftBorderVariant === 'red') {
    borderColor = theme.palette.red.main;
  } else if ($leftBorderVariant === 'green') {
    borderColor = theme.palette.success.main;
  } else if ($leftBorderVariant === 'gray') {
    borderColor = theme.palette.grey[400];
  } else {
    borderColor = undefined;
  }
  const hasBorder = borderColor !== null && borderColor !== undefined;
  return {
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    ...(hasBorder
      ? {
          borderLeftWidth: 4,
          borderLeftStyle: 'solid',
          borderLeftColor: borderColor,
        }
      : {}),
    '&:hover': {
      borderColor: theme.palette.grey[400],
      borderWidth: 1,
      ...(hasBorder
        ? {
            borderLeftWidth: 4,
            borderLeftStyle: 'solid',
            borderLeftColor: borderColor,
          }
        : {}),
    },
  };
});
